import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import TrustdService from "../service/TrustdService";
import StringUtils from "../utils/StringUtils";
import EmailUtils from "../utils/EmailUtils";
import ErrorUtils from "../utils/ErrorUtils";
import { findCountryCode, getCountryPhoneCodes } from "../utils/CountryCodes";
import { toE164Phone, toNationalPhone } from "../utils/PhoneUtils";
import { Button, Card, Field, Modal, Select } from "../components/UI";
import TermsAndConditions from "./TermsAndConditions";
import { apiHeaders } from "../hooks";
import { useAppContext } from "../context/AppContext";
import { useNavigate } from "../router";
import trustdLogo from "../assets/image.webp";

const STAGE_SIGNUP = "SIGNUP";
const STAGE_SIGNUP_VERIFY = "SIGNUP_VERIFY";
const STAGE_SIGNUP_HOSPITABLE = "SIGNUP_HOSPITABLE";
const STAGE_SIGNUP_ADVANCECM = "SIGNUP_ADVANCECM";
const STAGE_SIGNUP_SUCCESS = "SIGNUP_SUCCESS";

const initialForm = {
  username: "",
  password: "",
  passwordR: "",
  country: "",
  phone: "",
  phoneNumber: "",
  contactName: "",
  firstName: "",
  lastName: "",
  pms: "",
  numberOfProperties: "",
  state: "",
  writedCountry: "",
  company: "",
  sendEmail: "YES",
  channel: "",
  termsAndConditions: false,
  accountId: "",
  apiKey: "",
};

function LinkStage({ channel, onAuthorize }) {
  const title = channel === "advancecm" ? "AdvanceCM" : channel ? channel[0].toUpperCase() + channel.slice(1) : "";
  const logoSrc =
    channel === "advancecm"
      ? "https://scoreimage.s3.us-east-1.amazonaws.com/channel/advancecm.png"
      : "https://scoreimage.s3.us-east-1.amazonaws.com/channel/hospitable.png";

  return (
    <Card className="signupCard auth-modern-card auth-modern-card-wide">
      <div className="integration-link-card">
        <div className="text-center mb-3 integration-title">Link Your {title} Account With Trustd.ai</div>
        <div className="integration-logos">
          <img src="https://d2gl8sw234uklp.cloudfront.net/channel/trustd.png" alt="Trustd" />
          <div className="integration-dots"><span /><span /><span /></div>
          <img src={logoSrc} alt={title} />
        </div>
        <Button label="Authorize" onClick={onAuthorize} />
      </div>
    </Card>
  );
}

export default function SignUp({ code }) {
  const [signupForm, setSignupForm] = useState(initialForm);
  const [invitationCode, setInvitationCode] = useState(code || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [currentStage, setCurrentStage] = useState(STAGE_SIGNUP);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hostawayModal, setHostawayModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hostawayFieldErrors, setHostawayFieldErrors] = useState({});
  const termsRef = useRef(null);
  const prefilledInvitationCodeRef = useRef("");
  const countryPhoneCodes = getCountryPhoneCodes();
  const { commit, addToast } = useAppContext();
  const navigate = useNavigate();

  const splitContactName = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return { firstName: "", lastName: "" };
    const parts = normalized.split(/\s+/);
    const lastName = parts.pop() || "";
    const firstName = parts.join(" ");
    return { firstName, lastName };
  };

  const resolveActivePhoneCode = () => selectedCountry?.phoneCode || "1";
  const normalizeChannel = (value) => String(value || "").trim().toLowerCase();

  const normalizePhoneInput = (value) => toNationalPhone(value, resolveActivePhoneCode());

  const prefillFromInvitationCode = async (rawCode) => {
    const normalizedCode = String(rawCode || "").trim();
    if (!normalizedCode || prefilledInvitationCodeRef.current === normalizedCode) {
      return;
    }

    try {
      const invitation = await TrustdService.verifyInvitationCode(normalizedCode);
      const invitationEmail = String(invitation.email || "").trim().toLowerCase();
      const invitationName = String(invitation.contactName || "").trim();
      const { firstName, lastName } = splitContactName(invitationName);
      const invitationPhone = String(invitation.phoneNumber || "").trim();
      const normalizedPhone = toNationalPhone(invitationPhone, resolveActivePhoneCode());

      setSignupForm((form) => ({
        ...form,
        username: form.username || invitationEmail || "",
        company: form.company || invitation.company || "",
        firstName: form.firstName || firstName,
        lastName: form.lastName || lastName,
        contactName: form.contactName || invitationName || "",
        phone: form.phone || normalizedPhone || "",
        channel: form.channel || normalizeChannel(invitation.channel) || "",
        pms: form.pms || invitation.channel || "",
      }));

      if (invitationEmail) {
        await syncEmail(invitationEmail);
      }

      prefilledInvitationCodeRef.current = normalizedCode;
    } catch (error) {
      console.warn("prefillFromInvitationCode failed:", error);
    }
  };

  useEffect(() => {
    const country = findCountryCode("United States");
    if (country) setSelectedCountry(country);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("is_api") && urlParams.get("is_api") === "ownerrez") {
      setSignupForm((form) => ({ ...form, username: urlParams.get("reference") || "", channel: "ownerrez" }));
      addToast({ severity: "success", summary: "Success", detail: "Sign Up Successful", life: 5000 });
      setCurrentStage(STAGE_SIGNUP_VERIFY);
    }
    if (urlParams.has("email")) {
      syncEmail(urlParams.get("email"));
    }
    if (code) {
      prefillFromInvitationCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFieldError = (field, value, nextForm = signupForm) => {
    setFieldErrors((errors) => {
      if (!errors[field]) return errors;
      let shouldClear = !StringUtils.isEmpty(value);

      if (field === "username") shouldClear = EmailUtils.isValid(value);
      if (field === "password" || field === "passwordR") {
        const password = field === "password" ? value : nextForm.password;
        const confirmation = field === "passwordR" ? value : nextForm.passwordR;
        shouldClear = Boolean(password && confirmation && password === confirmation);
      }
      if (field === "termsAndConditions") shouldClear = Boolean(value);

      if (!shouldClear) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      if (field === "password" || field === "passwordR") {
        delete nextErrors.password;
        delete nextErrors.passwordR;
      }
      return nextErrors;
    });
  };

  const clearHostawayFieldError = (field, value) => {
    setHostawayFieldErrors((errors) => {
      if (!errors[field] || StringUtils.isEmpty(value)) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const updateForm = (field, value) => {
    setSignupForm((form) => {
      const nextForm = { ...form, [field]: value };
      clearFieldError(field, value, nextForm);
      clearHostawayFieldError(field, value);
      return nextForm;
    });
  };

  const updateInvitationCode = (value) => {
    setInvitationCode(value);
    clearFieldError("invitationCode", value);
  };

  const updateSelectedCountry = (country) => {
    setSelectedCountry(country);
    clearFieldError("countryCode", country?.phoneCountry);
  };

  const updateVerificationCode = (value) => {
    setVerificationCode(value);
    clearFieldError("verificationCode", value);
  };
  const onEnter = (handler) => (event) => {
    if (event.key === "Enter") {
      handler();
    }
  };

  const syncEmail = async (email) => {
    try {
      const [res, res2] = await Promise.all([
        axios.get(`/v1/users/${email}`, { headers: apiHeaders }),
        axios.get(`/v1/integrations/stripe/user-extra/${email}`, { headers: apiHeaders }),
      ]);

      const prefCountry = res2?.data ? findCountryCode(res2.data.country) : null;
      const preferredPhoneCode = prefCountry?.phoneCode || resolveActivePhoneCode();

      if (res.data) {
        const { firstName, lastName } = splitContactName(res.data.contactName);
        const rawPhone = String(res.data.phoneNumber || "").trim();
        const localPhone = toNationalPhone(rawPhone, preferredPhoneCode);
        setSignupForm((form) => ({
          ...form,
          firstName: form.firstName || firstName,
          lastName: form.lastName || lastName,
          username: form.username || res.data.email || res.data.username || "",
          company: form.company || res.data.company || "",
          contactName: form.contactName || res.data.contactName || "",
          channel: form.channel || normalizeChannel(res.data.channel) || "",
          phone: form.phone || localPhone,
        }));
      }

      if (res2.data) {
        if (prefCountry) {
          setSelectedCountry(prefCountry);
        }
        setSignupForm((form) => ({
          ...form,
          pms: form.pms || res2.data.pms || "",
          numberOfProperties: form.numberOfProperties || res2.data.numberOfProperties || "",
          state: form.state || res2.data.state || "",
          writedCountry: form.writedCountry || res2.data.country || "",
        }));
      }
    } catch (error) {
      console.error("syncEmail failed:", error);
    }
  };

  const buildForm = () => {
    if (!selectedCountry) return { ...signupForm, country: null, phoneNumber: null };
    return {
      ...signupForm,
      country: selectedCountry.phoneCountry,
      phone: normalizePhoneInput(signupForm.phone),
      phoneNumber: toE164Phone(signupForm.phone, selectedCountry.phoneCode),
      contactName: `${signupForm.firstName} ${signupForm.lastName}`,
    };
  };

  const validateSignup = (form) => {
    const failures = [];
    const errors = {};
    const addFailure = (field, detail) => {
      failures.push(detail);
      errors[field] = detail;
    };

    if (StringUtils.isEmpty(invitationCode)) addFailure("invitationCode", "Invitation Code is required");
    if (StringUtils.isEmpty(form.username)) addFailure("username", "Email is required");
    else if (!EmailUtils.isValid(form.username)) addFailure("username", "Email is not a valid email address");
    if (StringUtils.isEmpty(form.password)) addFailure("password", "Password is required");
    if (StringUtils.isEmpty(form.passwordR)) addFailure("passwordR", "Password (re-entry) is required");
    if (form.password && form.passwordR && form.password !== form.passwordR) addFailure("passwordR", "Password and Password Re-Entry do not match");
    if (StringUtils.isEmpty(form.firstName)) addFailure("firstName", "First Name is required");
    if (StringUtils.isEmpty(form.lastName)) addFailure("lastName", "Last Name is required");
    if (StringUtils.isEmpty(form.company)) addFailure("company", "Company is required");
    if (StringUtils.isEmpty(form.country)) addFailure("countryCode", "Country is required");
    if (StringUtils.isEmpty(form.phone)) addFailure("phone", "Phone Number is required");
    if (!form.termsAndConditions) addFailure("termsAndConditions", "Terms and Conditions should be accepted");

    failures.forEach((detail) => addToast({ severity: "error", summary: "Validation Error", detail, life: 5000 }));
    setFieldErrors(errors);
    return failures.length === 0;
  };

  const signup = async () => {
    if (loading) return;
    const form = buildForm();
    setSignupForm(form);
    if (!validateSignup(form)) return;

    setLoading(true);
    try {
      const verifiedInvitation = await TrustdService.verifyInvitationCode(invitationCode, form.username);
      const channelForm = { ...form, channel: normalizeChannel(verifiedInvitation.channel) };
      setSignupForm(channelForm);

      await TrustdService.saveUserInfo({
        contactName: channelForm.contactName,
        company: channelForm.company,
        email: channelForm.username,
        website: null,
        phoneNumber: channelForm.phoneNumber,
        address: null,
        sendEmail: channelForm.sendEmail,
        channel: channelForm.channel,
        accountId: channelForm.accountId,
        apiKey: channelForm.apiKey,
        country: channelForm.country,
        screening: 5,
        freeScreening: 0,
        status: "ACTIVE",
      });

      const signUpResult = await AuthService.signUp({
        ...channelForm,
        invitationCode,
      });
      if (signUpResult?.verificationCode) {
        setVerificationCode(signUpResult.verificationCode);
        addToast({
          severity: "info",
          summary: "Dev Verification Code",
          detail: `Use this code: ${signUpResult.verificationCode}`,
          life: 10000,
        });
      }

      await axios.post(
        "/v1/integrations/stripe/user-extra",
        {
          email: channelForm.username,
          pms: channelForm.pms,
          numberOfProperties: channelForm.numberOfProperties,
          state: channelForm.state,
          country: channelForm.writedCountry,
          signed: true,
        },
        { headers: apiHeaders },
      );

      await axios.post("/v1/users/onboard/user", { email: channelForm.username, invitationCode }, { headers: apiHeaders });

      addToast({ severity: "success", summary: "Success", detail: "Sign Up Successful", life: 5000 });
      setCurrentStage(STAGE_SIGNUP_VERIFY);
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (loading) return;
    if (StringUtils.isEmpty(verificationCode)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Verification Code is required", life: 5000 });
      setFieldErrors({ verificationCode: "Verification Code is required" });
      return;
    }

    setLoading(true);
    try {
      await AuthService.confirmSignUp(signupForm.username, verificationCode);
      await TrustdService.saveUserInfo({
        contactName: `${signupForm.firstName} ${signupForm.lastName}`,
        company: signupForm.company,
        email: signupForm.username,
        website: null,
        phoneNumber: signupForm.phoneNumber,
        address: null,
        sendEmail: signupForm.sendEmail,
        channel: signupForm.channel,
        accountId: signupForm.accountId,
        apiKey: signupForm.apiKey,
        screening: 5,
        freeScreening: 0,
        status: "ACTIVE",
      });

      const user = await AuthService.signIn(signupForm.username, signupForm.password);
      commit("signInUser", user);
      addToast({ severity: "success", summary: "Success", detail: "Code Verification Successful", life: 5000 });

      const channel = String(signupForm.channel || "").trim().toLowerCase();
      const hostEmail = String(signupForm.username || "").trim().toLowerCase();
      const contactName = `${signupForm.firstName} ${signupForm.lastName}`.trim();

      if (channel === "ownerrez") {
        const response = await TrustdService.ownerrezTempUserCreate({
          email: hostEmail,
          name: contactName,
          company: signupForm.company,
          phone: signupForm.phoneNumber || signupForm.phone,
          terms: "on",
        });

        if (response?.data?.status === "success" && response?.data?.redirect) {
          addToast({ severity: "success", summary: "Redirecting...", detail: response.data.message, life: 5000 });
          window.setTimeout(() => { window.location.href = response.data.redirect; }, 1500);
          return;
        }

        throw new Error(response?.data?.message || "OwnerRez authorization could not be started.");
      }

      if (channel === "hospitable") {
        setCurrentStage(STAGE_SIGNUP_HOSPITABLE);
        return;
      }

      if (channel === "advancecm") {
        setCurrentStage(STAGE_SIGNUP_ADVANCECM);
        return;
      }

      if (channel === "hostaway") {
        setHostawayModal(true);
        return;
      }

      navigate("/");
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const authorize = async () => {
    if (loading) return;
    setLoading(true);
    const channel = normalizeChannel(signupForm.channel);
    try {
      const res = channel === "hospitable"
        ? await TrustdService.redirectHospitable(signupForm.username)
        : await TrustdService.redirectTokeet(signupForm.username, channel);
      if (res.data.status === "success") {
        addToast({ severity: "success", summary: "Redirecting...", detail: res.data.message, life: 5000 });
        window.setTimeout(() => { window.location.href = res.data.redirect; }, 3000);
      } else {
        addToast({
          severity: "error",
          summary: "Error",
          detail: res?.data?.message || "Authorization start failed.",
          life: 10000,
        });
      }
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const completeHostaway = async () => {
    if (loading) return;
    const hostawayErrors = {};
    if (!String(signupForm.accountId || "").trim()) hostawayErrors.accountId = "Account ID is required";
    if (!String(signupForm.apiKey || "").trim()) hostawayErrors.apiKey = "API Key is required";
    if (Object.keys(hostawayErrors).length > 0) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Account ID and API Key are required.",
        life: 7000,
      });
      setHostawayFieldErrors(hostawayErrors);
      return;
    }
    setLoading(true);
    try {
      await TrustdService.saveUserInfo({
        contactName: `${signupForm.firstName} ${signupForm.lastName}`,
        company: signupForm.company,
        email: signupForm.username,
        website: null,
        phoneNumber: signupForm.phoneNumber,
        address: null,
        sendEmail: signupForm.sendEmail,
        channel: signupForm.channel,
        accountId: signupForm.accountId,
        apiKey: signupForm.apiKey,
        screening: 5,
        freeScreening: 0,
        status: "ACTIVE",
      });
      addToast({ severity: "success", summary: "Signup Completed", detail: "Redirecting to login page...", life: 5000 });
      window.setTimeout(() => navigate("/account"), 3000);
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const signupFields = (
    <>
      <h2 className="auth-modern-section-title">1. Host Details</h2>
      <Card className="signupCard signupFormCard auth-modern-card auth-modern-card-wide">
        <Field label="First Name" required className="mb-2" error={fieldErrors.firstName}><input className="form-control" placeholder="First Name" value={signupForm.firstName} autoComplete="given-name" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("firstName", event.target.value)} /></Field>
        <Field label="Last Name" required className="mb-2" error={fieldErrors.lastName}><input className="form-control" placeholder="Last Name" value={signupForm.lastName} autoComplete="family-name" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("lastName", event.target.value)} /></Field>
        <Field label="Invitation Code" required className="mb-2" error={fieldErrors.invitationCode}><input className="form-control" placeholder="Enter Invitation Code you received from us" value={invitationCode} autoCapitalize="characters" spellCheck="false" onKeyDown={onEnter(signup)} onChange={(event) => updateInvitationCode(event.target.value)} onBlur={() => { prefillFromInvitationCode(invitationCode); }} /></Field>
        <Field label="Email" required className="mb-2" error={fieldErrors.username}><input type="email" className="form-control" placeholder="Email" value={signupForm.username} autoComplete="email" autoCapitalize="none" spellCheck="false" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("username", event.target.value)} /></Field>
        <Field label="Password" required className="mb-2" error={fieldErrors.password}><input type="password" className="form-control" placeholder="Password" value={signupForm.password} autoComplete="new-password" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("password", event.target.value)} /></Field>
        <Field label="Re-Enter Password" required className="mb-2" error={fieldErrors.passwordR}><input type="password" className="form-control" placeholder="Re-Enter Password" value={signupForm.passwordR} autoComplete="new-password" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("passwordR", event.target.value)} /></Field>
        <Field label="Country Code" required className="mb-2" error={fieldErrors.countryCode}><Select value={selectedCountry} onChange={updateSelectedCountry} options={countryPhoneCodes} labelKey="label" valueKey="phoneCountry" placeholder="Select a Country Code" /></Field>
        <Field label="Phone Number" required className="mb-2" error={fieldErrors.phone}><input className="form-control" placeholder="5555555555" value={signupForm.phone} autoComplete="tel-national" inputMode="tel" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("phone", normalizePhoneInput(event.target.value))} /></Field>
      </Card>

      <h2 className="auth-modern-section-title">2. Property Details</h2>
      <Card className="signupCard signupFormCard auth-modern-card auth-modern-card-wide">
        <Field label="Property Management System - PMS" required className="mb-2"><input className="form-control" placeholder='Type "none" if you are not using one.' value={signupForm.pms} onKeyDown={onEnter(signup)} onChange={(event) => updateForm("pms", event.target.value)} /></Field>
        <Field label="Number Of Properties" required className="mb-2"><input type="number" className="form-control" placeholder="Number of properties" value={signupForm.numberOfProperties} inputMode="numeric" min="0" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("numberOfProperties", event.target.value)} /></Field>
        <Field label="Management Company" required className="mb-2" error={fieldErrors.company}><input className="form-control" placeholder="Company. Type Self if you do not have one." value={signupForm.company} autoComplete="organization" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("company", event.target.value)} /></Field>
        <Field label="Country" required className="mb-2"><input className="form-control" placeholder="Country" value={signupForm.writedCountry} autoComplete="country-name" onKeyDown={onEnter(signup)} onChange={(event) => updateForm("writedCountry", event.target.value)} /></Field>
        <Field label="City/State" required className="mb-2"><input className="form-control" placeholder="Property location (city/state)" value={signupForm.state} onKeyDown={onEnter(signup)} onChange={(event) => updateForm("state", event.target.value)} /></Field>
        <div className={`mb-3 signupButtons ${fieldErrors.termsAndConditions ? "has-error" : ""}`}>
          <div className="checkbox-container">
            <input type="checkbox" className="form-check-input" checked={signupForm.termsAndConditions} onChange={(event) => updateForm("termsAndConditions", event.target.checked)} />
            <label className="form-check-label">
              By clicking here, I state that I read, understood, and accepted the terms of{" "}
              <strong onClick={() => termsRef.current?.openHostServiceAgreement()}>Host Service Agreement</strong>,{" "}
              <strong onClick={() => termsRef.current?.openTermsOfUse()}>Terms of Use</strong>, and{" "}
              <strong onClick={() => termsRef.current?.openPrivacyPolicy()}>Privacy Policy</strong>.
            </label>
          </div>
          {fieldErrors.termsAndConditions ? <div className="field-error" role="alert">{fieldErrors.termsAndConditions}</div> : null}
          <div className="mt-3"><Button className="w-100 auth-modern-primary" label={loading ? "Creating Account..." : "Sign Up"} disabled={loading} onClick={signup} /></div>
          <div className="mt-3 centered-link auth-modern-inline-link">
            <a href="/login" onClick={(event) => { event.preventDefault(); navigate("/login"); }}>
              <span className="secondary-a">Have an account? </span>
              <span>Sign in</span>
            </a>
          </div>
        </div>
      </Card>
    </>
  );

  return (
    <>
      {loading ? <div className="loading-overlay"><div className="loader" /></div> : null}
      <div className="signupBody auth-screen auth-modern">
        <header className="auth-modern-topbar">
          <div className="auth-modern-brand">
            <img src={trustdLogo} alt="Trustd.ai" />
            <span>Next Generation Guest Intelligence</span>
          </div>
        </header>
        <main className="auth-modern-main auth-modern-main-wide">
          <div className="auth-modern-heading">
            <h1>Welcome to Trustd.ai Host Portal</h1>
            <p>Screen guests, verify identities, and manage your guest pipeline - all in one place.</p>
          </div>
          <div className="auth-modern-card-wrap auth-modern-signup-wrap">
              {currentStage === STAGE_SIGNUP ? signupFields : null}
              {currentStage === STAGE_SIGNUP_VERIFY ? (
                <Card className="signupCard auth-modern-card auth-modern-card-narrow">
                  <div className="text-center mb-3 verify-title">Verify Your Email</div>
                  <p className="verify-copy">
                    Please check your email for the verification code from Trustd Host Services, titled "Your Verification Code for Trustd Portal." If you do not see the email, kindly check your Junk or Spam folder.
                  </p>
                  <Field label="Verification Code" className="auth-modern-field" error={fieldErrors.verificationCode}>
                    <input className="form-control" placeholder="Verification Code" value={verificationCode} autoComplete="one-time-code" inputMode="numeric" onKeyDown={onEnter(verify)} onChange={(event) => updateVerificationCode(event.target.value)} />
                  </Field>
                  <div className="auth-modern-actions-row verify-actions">
                    <Button label={loading ? "Verifying..." : "Verify"} className="auth-modern-primary" disabled={loading} onClick={verify} />
                    <Button label="Reset" className="auth-modern-secondary" disabled={loading} onClick={() => setVerificationCode("")} />
                  </div>
                </Card>
              ) : null}
              {currentStage === STAGE_SIGNUP_HOSPITABLE ? <LinkStage channel="hospitable" onAuthorize={authorize} /> : null}
              {currentStage === STAGE_SIGNUP_ADVANCECM ? <LinkStage channel="advancecm" onAuthorize={authorize} /> : null}
              {currentStage === STAGE_SIGNUP_SUCCESS ? (
                <Card title="SignUp Successful" className="signupCard auth-modern-card auth-modern-card-narrow">
                  <p>Thanks for signing up for Trustd Portal Services. Your Account is ready for login.</p>
                  <Field label="Invitation Code"><input className="form-control" value={invitationCode} readOnly /></Field>
                  <Field label="Email"><input className="form-control" value={signupForm.username} readOnly /></Field>
                  <Field label="Name"><input className="form-control" value={`${signupForm.firstName} ${signupForm.lastName}`} readOnly /></Field>
                  <Field label="Company"><input className="form-control" value={signupForm.company} readOnly /></Field>
                  <Button label="Login" className="auth-modern-primary" onClick={() => navigate("/login")} />
                </Card>
              ) : null}
          </div>
          <div className="auth-modern-signals" aria-label="Trustd.ai capabilities">
            <span><i className="pi pi-verified" aria-hidden="true" />ID Verification</span>
            <span><i className="pi pi-shield" aria-hidden="true" />Instant Background Checks</span>
          </div>
        </main>
        <footer className="auth-modern-footer">© 2024 Trustd.ai · All rights reserved</footer>
      </div>
      <Modal title="Integration Details" description="Complete your Hostaway registration." icon="pi-info-circle" visible={hostawayModal} onClose={() => setHostawayModal(false)}>
        <p>Complete your registration.</p>
        <Field label="Account ID" error={hostawayFieldErrors.accountId}><input className="form-control" value={signupForm.accountId} onChange={(event) => updateForm("accountId", event.target.value)} /></Field>
        <Field label="API Key" error={hostawayFieldErrors.apiKey}><input className="form-control" value={signupForm.apiKey} onChange={(event) => updateForm("apiKey", event.target.value)} /></Field>
        <div className="modal-actions">
          <Button label="Cancel" className="btn-secondary" onClick={() => setHostawayModal(false)} />
          <Button label="Complete" onClick={completeHostaway} />
        </div>
      </Modal>
      <TermsAndConditions ref={termsRef} onAccept={() => updateForm("termsAndConditions", true)} />
    </>
  );
}
